import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  lazy,
  Suspense,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isExpired } from "react-jwt";
import axios from "axios";
import { getToken, refreshAccessToken } from "../services/auth";
import Loader from "../components/Loader/Loader";

const PublickRouters = lazy(() =>
  import("../router/combineRouters/PublickRouters")
);

const axiosWithAuth = axios.create();
/*
  DO NOT STORE AUTHENTICATION OR AUTHORIZATION RELATED INFORMATION IN STORAGE APIS (LOCALSTORAGE) (https://github.com/OWASP/CheatSheetSeries/blob/master/cheatsheets/HTML5_Security_Cheat_Sheet.md#local-storage)
*/

const AuthContext = React.createContext({
  token: null,
  refreshToken: null,
  idToken: null,
  rootOrganization: null,
  onReady: () => {},
  onAuthSucess: () => {},
  onAuthError: () => {},
  onAuthRefreshSuccess: () => {},
  onAuthRefreshError: () => {},
  onAuthLogout: () => {},
  onTokenExpired: () => {},
  // init: () => {}, //Promise
  updateToken: () => {}, //Promise minValidity
  authFields: {},
  isAuthField: false,
});

// const useQuery = () => new URLSearchParams(useLocation().search);

let Location = () => {
  return useLocation();
};

const useAuth = () => {
  const state = useContext(AuthContext);

  return { ...state };
};

const AuthProvider = ({ children }) => {
  let { pathname, search } = Location();
  const navigate = useNavigate();

  let [emplty, hash] = pathname.split("/");

  if (
    hash &&
    hash.length &&
    !hash.match(/^(?:[a-zA-Z0-9]{8,10})$/) &&
    hash !== "FAQ"
  ) {
    let validHashCode = hash.replace(/[^a-zA-Z0-9]/g, "");
    window.location.href = `/${validHashCode}${search}`;
  }

  const [state, setState] = useState({
    accessToken: null,
    authorizeDocToken: null,
    refreshToken: null,
    isAuthenticated: false,
    isNavigated: false,
    hashCode: hash,
    // rootOrganization: null,
    isRefreshTokenExpired: true,
    onTokenExpired: () => {},
    authFields: {},
    isAuthField: false,
    isDoc: false,
  });

  async function onTokenExpired(refreshToken) {
    try {
      let { data, status } = await refreshAccessToken(refreshToken);
      return {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      };
    } catch (err) {}
  }

  // Always-current token values for the axios interceptors below, so the
  // interceptors can be registered once instead of re-registered (and
  // leaked) on every render.
  const tokenRef = useRef({ accessToken: null, refreshToken: null });
  useEffect(() => {
    tokenRef.current = {
      accessToken: state.accessToken,
      refreshToken: state.refreshToken,
    };
  }, [state.accessToken, state.refreshToken]);

  const isRefreshingRef = useRef(false);
  const refreshSubscribersRef = useRef([]);

  const handleRefreshFailure = () => {
    localStorage.clear();
    window.location.reload();
  };

  // Dedupe concurrent refresh calls: if several requests 401 at once
  // (e.g. right after the tab has been idle), only one refresh call goes
  // out and the rest wait on it and retry with the new token.
  const refreshAuthToken = () => {
    if (isRefreshingRef.current) {
      return new Promise((resolve, reject) => {
        refreshSubscribersRef.current.push({ resolve, reject });
      });
    }

    isRefreshingRef.current = true;

    return refreshAccessToken(tokenRef.current.refreshToken)
      .then(({ data }) => {
        const newAccessToken = data.accessToken;
        const newRefreshToken = data.refreshToken;

        tokenRef.current = {
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        };
        localStorage.setItem("aT", newAccessToken);
        localStorage.setItem("rT", newRefreshToken);
        setState((prev) => ({
          ...prev,
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        }));

        refreshSubscribersRef.current.forEach(({ resolve }) =>
          resolve(newAccessToken)
        );
        refreshSubscribersRef.current = [];
        return newAccessToken;
      })
      .catch((err) => {
        refreshSubscribersRef.current.forEach(({ reject }) => reject(err));
        refreshSubscribersRef.current = [];
        handleRefreshFailure();
        throw err;
      })
      .finally(() => {
        isRefreshingRef.current = false;
      });
  };

  const getDomain = () => {
    const host = window.location.host;
    console.log(host, "hjsgdhsdsd");
    if (
      ["localhost", "dev-rls.mxfy.io"].some((domain) => host.includes(domain))
    ) {
      return "qa.mxfy.io";
    }
    return host;
  };

  useEffect(async () => {
    let persistedhashCode = localStorage.getItem("hC");
    let persistedRefreshToken = localStorage.getItem("rT");
    let persistedAccessToken = localStorage.getItem("aT");
    let isRefreshTokenExpired = true;

    if (persistedRefreshToken && persistedhashCode == hash) {
      //Check if refresh token is expired
      isRefreshTokenExpired = isExpired(localStorage.getItem("rT"));
    }

    if (
      (persistedRefreshToken && !isRefreshTokenExpired) ||
      (persistedAccessToken && !isRefreshTokenExpired)
    ) {
      const { refreshToken, accessToken } = await onTokenExpired(
        persistedRefreshToken
      );
      setState({
        ...state,
        accessToken,
        refreshToken,
        isAuthenticated: true,
      });
    } else if (!persistedAccessToken && hash) {
      const getAuthCode = async () => {
        const domain = getDomain();
        try {
          let { data, status, ...rest } = await getToken({ hash, domain });
          if (status === 200 && data.accessToken) {
            setState({
              accessToken: data.accessToken,
              refreshToken: data.refreshToken,
              isAuthenticated: true,
            });

            // localStorage.setItem("time", now.getTime() + 1 * 60 * 1000);
          }
          if (status === 200 && data.aut_enb) {
            let urlGet = document.URL;
            let path = "/" + urlGet.split("/").slice(4).join("/");
            navigate(`/?ref=${hash}&pt=${path}`);
          } else {
            localStorage.removeItem("AuthorizationToken");
          }
        } catch (err) {}
      };

      getAuthCode();
    }
  }, []);

  useEffect(() => {
    let persistedhashCode = localStorage.getItem("hC");
    let persistedRefreshToken = localStorage.getItem("rT");
    let persistedAccessToken = localStorage.getItem("aT");
    if (persistedRefreshToken) {
      localStorage.setItem("rT", persistedRefreshToken);
    } else {
      localStorage.setItem("rT", state.refreshToken);
    }

    if (
      !state.isAuthenticated &&
      persistedRefreshToken &&
      persistedAccessToken
    ) {
      setState({
        ...state,
        accessToken: persistedAccessToken,
        refreshToken: persistedRefreshToken,
        isAuthenticated: true,
        hashCode: persistedhashCode,
      });
    }

    localStorage.setItem("hC", hash);
  }, [state.isAuthenticated]);

  useEffect(() => {
    const requestInterceptor = axiosWithAuth.interceptors.request.use(
      (config) => {
        if (tokenRef.current.accessToken) {
          config.headers.authorization = "Bearer " + tokenRef.current.accessToken;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // On a 401 (e.g. the access token expired while the tab sat idle),
    // refresh the token and retry the original request instead of just
    // failing every call until a full page reload.
    const responseInterceptor = axiosWithAuth.interceptors.response.use(
      (response) => response,
      (error) => {
        const originalRequest = error.config;
        if (
          error.response &&
          error.response.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          tokenRef.current.refreshToken
        ) {
          originalRequest._retry = true;
          return refreshAuthToken().then((newAccessToken) => {
            originalRequest.headers.authorization = "Bearer " + newAccessToken;
            return axiosWithAuth(originalRequest);
          });
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosWithAuth.interceptors.request.eject(requestInterceptor);
      axiosWithAuth.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ ...state }}>
      {state.isAuthenticated ? (
        children
      ) : (
        <Suspense fallback={<Loader />}>
          <PublickRouters />
        </Suspense>
      )}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext, useAuth, axiosWithAuth };
